import { Button, Spinner, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { DocumentNameBody, DocumentStatus } from '@ipc/shared'
import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router'
import { api, isApiError } from '~/api/client'
import { keys, useFetchRegistration, useRegistration } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { FlowStepper } from '~/components/member/FlowStepper'
import { MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { pathForNext, pathForStatus, registrationEndedPath } from '~/lib/flow'
import { useConfigGate, useFlowSteps } from '~/lib/steps'
import { DocumentDone } from './DocumentStep'
import { NameCheck } from './NameCheck'
import { StepSkeleton } from '~/components/member/StepSkeleton'

/**
 * Every 2 seconds, for 90 seconds, then the page stops asking and says to come back later (spec §12.1 step 4). A 429
 * (the per-member return limit) stops the polling at once with the same line: the server's poller keeps collecting.
 */
export const POLL_MS = 2000
export const POLL_MAX = 45

/**
 * What the provider calls the session in its return address: Didit `verificationSessionId`, ID Analyzer `reference`,
 * the fake provider `ref`. The first one present is sent as a hint; the server only uses it to look at the member's
 * own open try (spec §3.3).
 */
/** How long "Thank you. A person will check it." stays before the next step. */
const DISPUTED_MS = 1200

export const RETURN_PARAMS = ['verificationSessionId', 'reference', 'ref'] as const

/** `/join/document/return`: back from the provider's page. Checking, then the name check, then on. */
export default function DocumentReturn() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [params] = useSearchParams()
  const { config, failed, pending } = useConfigGate()
  const { required } = useFlowSteps()
  const reg = useRegistration()
  const fetchRegistration = useFetchRegistration()
  const [status, setStatus] = useState<DocumentStatus | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [slow, setSlow] = useState(false)
  // Bumped by "Check again": the polling effect starts over.
  const [epoch, setEpoch] = useState(0)
  const [answered, setAnswered] = useState<DocumentNameBody['answer'] | null>(null)

  // The hint is read once: the address of the page is the same for as long as it is open.
  const ref = useRef<string | undefined>(undefined)
  if (ref.current === undefined) {
    const found = RETURN_PARAMS.map((k) => params.get(k)).find((v) => v)
    ref.current = found ? found.slice(0, 200) : ''
  }

  useEffect(() => {
    if (!config.isSuccess || !required) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let polls = 0
    const tick = async () => {
      let waiting = true
      try {
        const body = ref.current ? { ref: ref.current } : {}
        const s = await api('registrationDocumentReturn', { body })
        if (cancelled) return
        setStatus(s)
        setError(null)
        qc.setQueryData(keys.document, s)
        // `in_progress`: the scan was never opened (nothing can arrive), so only `checking` is waited for.
        waiting = s.state === 'checking'
      } catch (err) {
        if (cancelled) return
        setError(err)
        // A refusal that will not change (not signed in, not allowed, too many requests) ends the polling; a 429
        // says at once that it takes longer. A hiccup (5xx, network) is tried again.
        if (isApiError(err) && err.status < 500) {
          waiting = false
          if (err.status === 429) setSlow(true)
        }
      }
      if (!waiting) return
      polls += 1
      if (polls >= POLL_MAX) setSlow(true)
      else timer = setTimeout(() => void tick(), POLL_MS)
    }
    void tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [config.isSuccess, required, epoch, qc])

  const answer = useMutation({
    mutationFn: async (a: DocumentNameBody['answer']) => {
      const res = await api('registrationDocumentName', { body: { attemptId: status!.attempt!.id, answer: a } })
      const r = await fetchRegistration()
      return pathForNext(r.provider, res.next)
    },
    onMutate: (a) => setAnswered(a),
    // A "Not right" answer shows its thank-you line for a moment before the next step (a person looks at the name).
    onSuccess: (to, a) => {
      if (a === 'disputed') setTimeout(() => navigate(to), DISPUTED_MS)
      else navigate(to)
    },
    onSettled: (_d, err) => {
      if (err) setAnswered(null)
    },
  })

  if (pending) return null
  // A failed config read is not "the step is off": say so and let the member retry (the return link is one-shot).
  if (failed)
    return (
      <MemberLayout flow>
        <RetryError error={config.error} onRetry={() => void config.refetch()} />
      </MemberLayout>
    )
  // The policy was switched off meanwhile: the step page settles a registration still at `document_pending`.
  if (!required) return <Navigate to={reg.data ? `/join/${reg.data.provider}/document` : '/join'} replace />
  // The registration is claimed only after the document step: a 401 here is the registration cookie ending.
  if (isApiError(error) && error.status === 401)
    return <Navigate {...registrationEndedPath(reg.data?.provider)} replace />

  let body
  const s = status
  if (slow && (s === null || s.state === 'checking')) {
    body = (
      <Stack gap={4}>
        <Text role='status'>{t('join.document.slow')}</Text>
        <ActionBar>
          <Button
            size='lg'
            w='full'
            onClick={() => {
              setSlow(false)
              setError(null)
              setEpoch((e) => e + 1)
            }}
          >
            {t('join.document.checkAgain')}
          </Button>
        </ActionBar>
        {reg.data && (
          <Button asChild variant='outline' alignSelf='flex-start'>
            <RouterLink to={`/join/${reg.data.provider}/document`}>{t('join.document.slowBackStep')}</RouterLink>
          </Button>
        )}
      </Stack>
    )
  } else if (s === null) {
    body = error ? (
      <RetryError
        error={error}
        onRetry={() => {
          setError(null)
          setEpoch((e) => e + 1)
        }}
      />
    ) : (
      <Text role='status'>
        <Spinner size='sm' me={2} />
        {t('join.document.checking')}
      </Text>
    )
  } else if (s.state === 'confirm_name' && s.attempt?.nameShort) {
    body = (
      <>
        <NameCheck
          name={s.attempt.nameShort}
          pending={answer.isPending || answer.isSuccess ? answered : null}
          recognised={s.attempt.recognised?.class ?? null}
          disputed={answer.isSuccess && answered === 'disputed'}
          onAnswer={(a) => answer.mutate(a)}
        />
        {answer.isError && <ErrorAlert>{friendly(answer.error)}</ErrorAlert>}
      </>
    )
  } else if (s.state === 'exempt' || s.state === 'evidence_ready' || s.state === 'not_needed' || s.state === 'manual') {
    body = <DocumentDone state={s.state} />
  } else if (s.state === 'checking' || s.state === 'confirm_name') {
    body = (
      <Text role='status'>
        <Spinner size='sm' me={2} />
        {t('join.document.checking')}
      </Text>
    )
  } else {
    // needed, retry, in_progress (the scan was never opened) or not_needed: nothing is being checked. The step page
    // shows what to do (a failed try, or the scan offered again).
    if (!s.applies && reg.data) return <Navigate to={pathForStatus(reg.data)} replace />
    if (reg.data) return <Navigate to={`/join/${reg.data.provider}/document`} replace />
    if (reg.isError) {
      if (isApiError(reg.error) && reg.error.status === 401) {
        const ended = registrationEndedPath()
        return <Navigate to={ended.to} state={ended.state} replace />
      }
      body = <RetryError error={reg.error} onRetry={() => void reg.refetch()} />
    } else body = <StepSkeleton />
  }

  return (
    <MemberLayout flow>
      <FlowStepper step='document' />
      {body}
    </MemberLayout>
  )
}
